<template>
  <div class="musubi-page musubi-page--drafts">

    <!-- ══════════════ LIST VIEW (/acquire/drafts) ══════════════ -->
    <template v-if="!draftId">
      <header class="musubi-page__head">
        <h1><CygnusIcon name="bolt" class="ai-ico" /> AI campaign drafts</h1>
        <p class="text-tertiary">
          Every draft the generator has produced. Nothing here runs — a draft becomes real
          only when you approve it, and even then it creates a <strong>paused</strong> flow.
        </p>
      </header>

      <div class="dl-hero">
        <div class="dl-hero__tag"><CygnusIcon name="bolt" /> AI</div>
        <div class="dl-hero__body">
          <strong>Start a campaign in a sentence.</strong>
          Describe who to find and what you're offering on the
          <a href="/acquire/pipeline-wizard">New pipeline</a> page — we draft it, you review here.
        </div>
      </div>

      <MusubiSectionCard title="Your drafts" icon="layer-group">
        <template #actions>
          <button class="btn btn-sm btn-ghost me-2" @click="toggleArchived">
            <CygnusIcon :name="showArchived ? 'eye' : 'box'" />
            {{ showArchived ? 'Hide archived' : 'Show archived' }}
          </button>
          <a class="btn btn-sm btn-ai" href="/acquire/pipeline-wizard"><CygnusIcon name="bolt" class="me-1" />New draft</a>
        </template>

        <div v-if="!loadingList && drafts.length" class="dl-toolbar">
          <input v-model="search" class="dl-input" placeholder="Search title, brief or source…">
          <select v-model="typeFilter" class="dl-select">
            <option value="">Any source type</option>
            <option v-for="t in typeOptions" :key="t" :value="t">{{ sourceLabel(t) }}</option>
          </select>
          <select v-model="statusFilter" class="dl-select">
            <option value="">Any status</option>
            <option v-for="st in statusOptions" :key="st" :value="st">{{ statusLabel(st) }}</option>
          </select>
          <button v-if="filtersActive" type="button" class="btn btn-sm btn-link p-0" @click="clearFilters">Clear</button>
        </div>

        <div v-if="loadingList" class="text-tertiary small py-2">Loading drafts…</div>
        <p v-else-if="!filteredDrafts.length" class="dl-empty">
          <template v-if="drafts.length">No drafts match these filters.</template>
          <template v-else-if="showArchived">No archived drafts.</template>
          <template v-else>No drafts yet. <a href="/acquire/pipeline-wizard">Describe your first campaign →</a></template>
        </p>
        <div v-else class="dl-list">
          <div v-for="d in filteredDrafts" :key="d.id" class="dl-row" :class="{ 'dl-row--archived': d.archived_at }">
            <a class="dl-row__link" :href="`/acquire/drafts/${d.id}`">
              <span class="dl-row__body">
                <span class="dl-row__title">{{ d.campaign_title || d.prompt_text || ('Draft #' + d.id) }}</span>
                <span class="dl-row__meta">
                  <CygnusIcon name="note-sticky" /> {{ d.prompt_text }}
                </span>
              </span>
              <!-- Approved drafts have a real crawl source behind them, so show
                   what it actually found rather than only what we predicted. -->
              <template v-if="d.discovery">
                <span class="dl-row__num dl-row__num--found">
                  <span class="dl-row__n">{{ (d.discovery.discovered || 0).toLocaleString() }}</span>
                  <span class="dl-row__nt">found</span>
                </span>
                <span class="dl-row__num dl-row__num--found">
                  <span class="dl-row__n">{{ (d.discovery.enriched || 0).toLocaleString() }}</span>
                  <span class="dl-row__nt">enriched</span>
                </span>
              </template>
              <span v-else class="dl-row__num">
                <span class="dl-row__n">≈ {{ (d.match_estimate || 0).toLocaleString() }}</span>
                <span class="dl-row__nt">match</span>
              </span>
              <ConfidenceBars :level="d.confidence" compact />
              <MusubiStatusPill :status="d.status" :tone="statusTone(d.status)" :label="statusLabel(d.status)" />
            </a>
            <button
              v-if="d.archived_at"
              class="dl-row__arch" title="Restore this draft"
              :disabled="rowBusy === d.id" @click="restoreRow(d)"
            ><CygnusIcon name="refresh" /></button>
            <button
              v-else
              class="dl-row__arch" title="Archive this draft"
              :disabled="rowBusy === d.id" @click="archiveRow(d)"
            ><CygnusIcon name="box" /></button>
          </div>
        </div>
      </MusubiSectionCard>
    </template>

    <!-- ══════════════ REVIEW VIEW (/acquire/drafts/{id}) ══════════════ -->
    <template v-else>
      <div v-if="loading" class="text-tertiary small py-4">Loading draft…</div>
      <div v-else-if="loadError" class="alert alert-danger py-2 small">{{ loadError }}</div>

      <div v-else-if="draft" class="draft-grid">
        <!-- ── Draft main ── -->
        <div class="draft-main">
          <div class="dh-crumb"><a href="/acquire/drafts">AI drafts</a> <CygnusIcon name="chevron-right" /> Review</div>
          <div class="draft-head">
            <h1 class="draft-title">{{ payload.campaign_title || 'Campaign draft' }}</h1>
            <span class="ai-tag"><CygnusIcon name="bolt" /> AI-generated</span>
          </div>

          <div class="dh-pills">
            <MusubiStatusPill
              :status="draft.status"
              :tone="statusTone(draft.status)"
              :label="draft.status === 'draft' ? 'Draft — nothing runs until you approve' : statusLabel(draft.status)" />
            <span class="conf" :title="confidenceReasonsText">
              <ConfidenceBars :level="draft.confidence" />
              <span class="conf__t">{{ confTitle }} confidence</span>
            </span>
            <span class="receipt"><CygnusIcon name="bolt" /> used {{ draft.tokens_used }} token{{ draft.tokens_used === 1 ? '' : 's' }}</span>
          </div>

          <!-- Plan summary -->
          <div class="plan-summary">
            <CygnusIcon name="bolt" class="plan-summary__ico" />
            <p>
              {{ payload.plan_summary }}
              <span class="plan-summary__n">Around <strong>{{ (draft.match_estimate || 0).toLocaleString() }}</strong> companies match today.</span>
            </p>
          </div>

          <!-- Reasoning -->
          <details v-if="payload.reasoning" class="reason">
            <summary><CygnusIcon name="bolt" /> Reasoning — why these codes &amp; this region</summary>
            <p>{{ payload.reasoning }}</p>
          </details>

          <!-- Confidence reasons -->
          <details v-if="confidenceReasons.length" class="reason">
            <summary><CygnusIcon name="gauge" /> How we derived “{{ confTitle }}” confidence</summary>
            <ul class="reason__list">
              <li v-for="(r, i) in confidenceReasons" :key="i">{{ r }}</li>
            </ul>
          </details>

          <h2 class="dsec-h">The draft</h2>

          <!-- Card 1 — Source -->
          <div class="dcard">
            <div class="dcard-h">
              <span class="step">Step 1</span><h4>Where to look</h4>
              <button v-if="canEdit && !editing.source" class="dcard-edit" @click="startEdit('source')"><CygnusIcon name="pen" /> Edit</button>
            </div>
            <template v-if="!editing.source">
              <div class="kv"><span class="k">Source</span><span class="v">{{ sourceName }} · {{ sourceTypeLabel }}</span></div>
              <div class="kv"><span class="k">Region</span><span class="v">{{ regionSummary }}</span></div>
              <p v-if="payload.source && payload.source.location_hint" class="covnote">Crawl centred on {{ payload.source.location_hint }}.</p>
            </template>
            <div v-else class="dedit">
              <label class="dfield"><span class="dfield__l">Source name</span>
                <input v-model.trim="edit.source.name" type="text" class="dinput" placeholder="e.g. Leeds accountants sweep"></label>
              <div class="dfield-grid">
                <label class="dfield"><span class="dfield__l">Type</span>
                  <select v-model="edit.source.type" class="dinput">
                    <option value="local_search">Local search</option>
                    <option value="google_places">Google Places sweep</option>
                  </select></label>
                <label class="dfield"><span class="dfield__l">Location hint</span>
                  <input v-model.trim="edit.source.location_hint" type="text" class="dinput" placeholder="e.g. Leeds, UK"></label>
              </div>
              <div class="dedit__act">
                <button class="btn btn-sm btn-commit" :disabled="saving" @click="saveEdit('source')"><CygnusIcon name="check" class="me-1" />Save</button>
                <button class="btn btn-sm btn-ghost" :disabled="saving" @click="cancelEdit('source')">Cancel</button>
              </div>
            </div>
          </div>

          <!-- Card 2 — Audience -->
          <div class="dcard">
            <div class="dcard-h">
              <span class="step">Step 2</span><h4>Who to target</h4>
              <button v-if="canEdit && !editing.audience" class="dcard-edit" @click="startEdit('audience')"><CygnusIcon name="pen" /> Edit</button>
            </div>
            <template v-if="!editing.audience">
              <div class="tagrow">
                <span v-for="(row, i) in industryGrounding" :key="'ind'+i" class="sic" :class="{ 'sic--miss': !row.resolved }">
                  <span class="code">{{ row.tokens.length ? row.tokens.join(' · ') : '?' }}</span>
                  {{ row.term }}
                </span>
                <span v-for="(row, i) in locationGrounding" :key="'loc'+i" class="sic sic--geo" :class="{ 'sic--miss': !row.resolved }">
                  <span class="code">{{ row.areas.length ? row.areas.join(' · ') : '?' }}</span>
                  {{ row.term }}
                </span>
                <span v-if="groundedMinEmployees > 0" class="sic sic--new">
                  <span class="code">≥{{ groundedMinEmployees }}</span> minimum staff
                </span>
              </div>
              <div class="matchline">
                <span class="matchline__n">≈ {{ (liveEstimate !== null ? liveEstimate : draft.match_estimate || 0).toLocaleString() }}</span>
                <span class="matchline__t">companies match today</span>
                <span v-if="estimateBusy" class="matchline__busy">refreshing…</span>
              </div>
            </template>
            <div v-else class="dedit">
              <label class="dfield"><span class="dfield__l">Industries</span>
                <input v-model.trim="edit.audience.industry" type="text" class="dinput" placeholder="e.g. accountants, bookkeepers">
                <span class="dfield__h">Comma-separated. We resolve these to SIC codes.</span></label>
              <div class="dfield-grid">
                <label class="dfield"><span class="dfield__l">Locations</span>
                  <input v-model.trim="edit.audience.location" type="text" class="dinput" placeholder="e.g. Leeds, York">
                  <span class="dfield__h">UK counties/cities/towns, comma-separated.</span></label>
                <label class="dfield"><span class="dfield__l">Minimum staff</span>
                  <input v-model.number="edit.audience.min_employees" type="number" min="0" class="dinput" placeholder="0"></label>
                <label class="dfield"><span class="dfield__l">Company status</span>
                  <select v-model="edit.audience.company_status" class="dinput">
                    <option value="">Any status</option>
                    <option value="active">active</option>
                    <option value="dissolved">dissolved</option>
                    <option value="liquidation">liquidation</option>
                    <option value="administration">administration</option>
                  </select></label>
              </div>
              <div class="dedit__act">
                <button class="btn btn-sm btn-commit" :disabled="saving" @click="saveEdit('audience')"><CygnusIcon name="check" class="me-1" />Save</button>
                <button class="btn btn-sm btn-ghost" :disabled="saving" @click="cancelEdit('audience')">Cancel</button>
              </div>
            </div>
          </div>

          <!-- Card 3 — Flow -->
          <div class="dcard">
            <div class="dcard-h">
              <span class="step">Step 3</span><h4>Flow</h4>
              <button v-if="canEdit && !editing.flow" class="dcard-edit" @click="startEdit('flow')"><CygnusIcon name="pen" /> Edit</button>
            </div>
            <div v-if="!editing.flow" class="flow">
              <template v-for="(s, i) in flowSteps" :key="i">
                <span v-if="s.type === 'email'" class="fstep fstep--mail">
                  <CygnusIcon name="envelope" />
                  <span class="fstep__l">{{ s.label || 'Email' }}</span>
                  <span class="fstep__s">{{ s.subject }}</span>
                </span>
                <span v-else-if="s.type === 'wait'" class="fstep fstep--wait">
                  <CygnusIcon name="clock" />{{ s.days }} day{{ Number(s.days) === 1 ? '' : 's' }}
                </span>
                <CygnusIcon name="arrow-right" class="farrow" v-if="i < flowSteps.length - 1" />
              </template>
            </div>
            <div v-else class="dedit">
              <label class="dfield"><span class="dfield__l">Flow name</span>
                <input v-model.trim="edit.flow.name" type="text" class="dinput"></label>
              <div v-for="(s, i) in edit.flow.steps" :key="i" class="fstep-edit">
                <div class="fstep-edit__h">
                  <span class="fstep-edit__n">Step {{ i + 1 }}</span>
                  <span class="fstep-edit__type">{{ s.type === 'email' ? 'Email' : 'Wait' }}</span>
                  <button class="fstep-edit__rm" title="Remove step" @click="removeFlowStep(i)"><CygnusIcon name="trash" /></button>
                </div>
                <template v-if="s.type === 'email'">
                  <input v-model.trim="s.subject" type="text" class="dinput mb-1" placeholder="Subject">
                  <textarea v-model="s.body_text" rows="4" class="dinput" placeholder="Email body — use {first_name}, {company_name}"></textarea>
                </template>
                <label v-else class="dfield dfield--inline"><span class="dfield__l">Wait</span>
                  <input v-model.number="s.days" type="number" min="1" class="dinput dinput--sm"> <span class="dfield__h">days</span></label>
              </div>
              <div class="fstep-add">
                <button class="btn btn-xs btn-ghost" @click="addFlowStep('email')"><CygnusIcon name="envelope" class="me-1" />Add email</button>
                <button class="btn btn-xs btn-ghost" @click="addFlowStep('wait')"><CygnusIcon name="clock" class="me-1" />Add wait</button>
              </div>
              <div class="dedit__act">
                <button class="btn btn-sm btn-commit" :disabled="saving" @click="saveEdit('flow')"><CygnusIcon name="check" class="me-1" />Save</button>
                <button class="btn btn-sm btn-ghost" :disabled="saving" @click="cancelEdit('flow')">Cancel</button>
              </div>
            </div>
          </div>

          <!-- Card 4 — Content -->
          <div class="dcard">
            <div class="dcard-h">
              <span class="step">Step 4</span><h4>Content — opener email</h4>
              <button v-if="canEdit && !editing.content" class="dcard-edit" @click="startEdit('content')"><CygnusIcon name="pen" /> Edit</button>
            </div>
            <template v-if="!editing.content">
              <div class="email-prev">
                <div class="email-head">
                  <span class="email-head__k">Subject</span>
                  <span class="email-head__v"><template v-for="(part, i) in tokenise(content.subject)" :key="i"><span v-if="part.token" class="mtoken">{{ part.text }}</span><template v-else>{{ part.text }}</template></template></span>
                </div>
                <div class="email-body">
                  <p v-for="(para, i) in bodyParagraphs" :key="i">
                    <template v-for="(part, j) in tokenise(para)" :key="j"><span v-if="part.token" class="mtoken">{{ part.text }}</span><template v-else>{{ part.text }}</template></template>
                  </p>
                </div>
              </div>
              <p class="covnote">Merge tokens like <span class="mtoken">first_name</span> fill with each prospect's real data at send time.</p>
            </template>
            <div v-else class="dedit">
              <label class="dfield"><span class="dfield__l">Subject</span>
                <input v-model.trim="edit.content.subject" type="text" class="dinput"></label>
              <label class="dfield"><span class="dfield__l">Body</span>
                <textarea v-model="edit.content.body_text" rows="8" class="dinput"></textarea>
                <span class="dfield__h">Merge tokens like <span class="mtoken">first_name</span> fill each prospect's real data at send time. This also updates the opener step above.</span></label>
              <div class="dedit__act">
                <button class="btn btn-sm btn-commit" :disabled="saving" @click="saveEdit('content')"><CygnusIcon name="check" class="me-1" />Save</button>
                <button class="btn btn-sm btn-ghost" :disabled="saving" @click="cancelEdit('content')">Cancel</button>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="draft-foot">
            <div v-if="approveResult" class="alert alert-success py-2 small mb-2">
              {{ approveResult }}
              <span v-if="createdLinks.flow"> · <a :href="`/outreach/drip-flows?flow=${createdLinks.flow}`">Open the flow</a></span>
            </div>
            <div v-if="actionError" class="alert alert-danger py-2 small mb-2">{{ actionError }}</div>
            <div v-if="editError" class="alert alert-danger py-2 small mb-2">{{ editError }}</div>

            <template v-if="draft.status === 'draft'">
              <!-- How the created flow should start. Default = paused (current
                   behaviour); discovery is an opt-in "gather without sending". -->
              <div class="startmode">
                <label class="startmode__opt" :class="{ on: !startDiscovery }">
                  <input type="radio" :value="false" v-model="startDiscovery">
                  <span><strong>Start when ready</strong> — creates a paused flow you activate later.</span>
                </label>
                <label class="startmode__opt" :class="{ on: startDiscovery }">
                  <input type="radio" :value="true" v-model="startDiscovery">
                  <span><strong>Start in discovery mode</strong> — the crawler gathers + enriches and we count who'd qualify, but nothing sends until you go live.</span>
                </label>
              </div>
              <div class="actions">
                <button class="btn btn-commit" :disabled="busy || anyEditing" @click="approve">
                  <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy === 'approve'" />
                  <CygnusIcon name="check" class="me-1" v-else />Approve &amp; create pipeline
                </button>
                <button v-if="!rejecting" class="btn btn-ghost" :disabled="busy" @click="rejecting = true">Reject</button>
                <span class="ph-spacer"></span>
                <button class="btn btn-ghost btn-arch" :disabled="busy" @click="archiveCurrent"><CygnusIcon name="box" class="me-1" />Archive</button>
              </div>
              <p v-if="anyEditing" class="editnote"><CygnusIcon name="info-circle" class="me-1" />Finish or cancel your edit before approving.</p>
              <p class="fineprint">
                <CygnusIcon name="warning" />
                <span v-if="startDiscovery">Approving creates the source, an email template and a <strong>DISCOVERING</strong> flow —
                it gathers + counts matches but sends nothing until you go live.</span>
                <span v-else>Approving creates the source, an email template and a <strong>PAUSED</strong> flow —
                sending still requires you to activate it. Nothing sends now.</span>
              </p>

              <div v-if="rejecting" class="reject-box">
                <label class="reject-box__lbl">Why are you rejecting this draft?</label>
                <textarea v-model="rejectReason" rows="2" class="form-control form-control-sm" placeholder="e.g. Region too broad — I'll re-brief"></textarea>
                <div class="reject-box__act">
                  <button class="btn btn-sm btn-ghost" :disabled="busy" @click="rejecting = false">Cancel</button>
                  <button class="btn btn-sm btn-danger" :disabled="busy || !rejectReason.trim()" @click="reject">
                    <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy === 'reject'" />Confirm reject
                  </button>
                </div>
              </div>
            </template>

            <div v-else class="terminal-state">
              <MusubiStatusPill :status="draft.status" :tone="statusTone(draft.status)" :label="statusLabel(draft.status)" />
              <span v-if="draft.status === 'approved' && createdLinks.flow" class="text-tertiary small">
                Created a {{ (draft.result_refs || {}).flow_status === 'discovering' ? 'discovering' : 'paused' }} flow —
                <a :href="createdLinks.flow ? `/outreach/drip-flows?flow=${createdLinks.flow}` : '/outreach/drip-flows'">open it</a>
                {{ (draft.result_refs || {}).flow_status === 'discovering' ? 'to go live when ready.' : 'to activate when ready.' }}
              </span>
              <span v-if="draft.status === 'rejected' && (draft.result_refs || {}).rejected_reason" class="text-tertiary small">
                Reason: {{ draft.result_refs.rejected_reason }}
              </span>
              <span class="ph-spacer"></span>
              <button v-if="!draft.archived_at" class="btn btn-sm btn-ghost btn-arch" :disabled="busy" @click="archiveCurrent"><CygnusIcon name="box" class="me-1" />Archive</button>
              <button v-else class="btn btn-sm btn-ghost" :disabled="busy" @click="restoreCurrent"><CygnusIcon name="refresh" class="me-1" />Restore</button>
            </div>
          </div>
        </div>

        <!-- ── Refine rail ── -->
        <aside class="refine">
          <div class="refine-h">
            <CygnusIcon name="bolt" />
            <div>
              <strong>Refine this draft</strong>
              <span>Ask for changes in plain English. Edits apply to the cards on the left — nothing runs.</span>
            </div>
          </div>

          <div class="refine-body" ref="refineBody">
            <div class="rday">Version history</div>
            <template v-for="(v, i) in versions" :key="i">
              <!-- Manual (human) edit — indigo, NOT teal AI -->
              <div v-if="v.source === 'manual'" class="msg msg--manual">
                <div class="mbub">
                  <span class="mhead"><CygnusIcon name="pen" /> You edited</span>
                  <template v-if="(v.changed_paths || []).length">Changed {{ (v.changed_paths || []).join(', ') }}.</template>
                  <template v-else>Edited the draft by hand.</template>
                </div>
              </div>
              <template v-else>
                <div v-if="v.refine_prompt" class="msg msg--user">
                  <div class="ubub">{{ v.refine_prompt }}</div>
                </div>
                <div class="msg msg--ai">
                  <div class="abub">
                    <span class="ahead"><CygnusIcon name="bolt" /> Yorlin</span>
                    <template v-if="i === 0">Drafted the campaign from your brief.</template>
                    <template v-else>
                      Updated the draft.
                      <span v-if="(v.changed_paths || []).length" class="chg"><CygnusIcon name="pen" /> {{ (v.changed_paths || []).join(', ') }}</span>
                    </template>
                  </div>
                </div>
              </template>
            </template>
          </div>

          <div v-if="draft.status === 'draft'" class="refine-in">
            <div v-if="refineError" class="small mb-1" :class="aiDown ? 'dl-ai-down' : 'text-danger'">
              <CygnusIcon v-if="aiDown" name="triangle-exclamation" class="me-1" />{{ refineError }}
            </div>
            <textarea
              v-model="refineText" rows="2" class="rin form-control form-control-sm"
              placeholder="e.g. Only target firms with 5+ staff" :disabled="busy"></textarea>
            <div class="refine-in__foot">
              <span class="rcost"><CygnusIcon name="bolt" /> Each refine uses a few AI tokens (≈ 3–8)</span>
              <button class="btn btn-sm btn-ai" :disabled="busy || !refineText.trim()" @click="refine">
                <CygnusIcon name="spinner" :spin="true" v-if="busy === 'refine'" />
                <CygnusIcon name="envelope" v-else />
              </button>
            </div>
          </div>
          <div v-else class="refine-in refine-in--closed">
            <CygnusIcon name="lock" class="me-1" /> This draft is {{ draft.status }} — refining is closed.
          </div>
        </aside>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * AdminCampaignDrafts — the AI campaign generator's triage surface
 * (usability-refocus §5a, mockup screen B). ONE component serves both the list
 * (/acquire/drafts) and the review view (/acquire/drafts/{id}); it reads the id
 * from the path. Everything is honest: teal marks AI actions, indigo marks the
 * single commit action (Approve), the "Draft — nothing runs until you approve"
 * pill + PAUSED fineprint are always visible, confidence is shown with its
 * derived reasons, and every token cost is stated. No native dialogs.
 *
 * API (all Ops::ACQUIRE): GET /admin/drafts, GET /admin/drafts/{id},
 * POST /admin/drafts/{id}/refine|approve|reject.
 */
import useApi from "@apexl/icarus-core/src/services/api";
import { apiErrorMessage, isAiUnavailable } from "./util/aiError";
import MusubiSectionCard from "@root/components/ui/MusubiSectionCard.vue";
import MusubiStatusPill from "@root/components/ui/MusubiStatusPill.vue";

const SOURCE_LABELS = {
  google_places: 'Google Places sweep',
  local_search: 'Local search',
  ch_sic: 'Companies House by SIC',
};
const STATUS_LABELS = { draft: 'Draft', approved: 'Approved', rejected: 'Rejected', superseded: 'Superseded' };
const STATUS_TONE = { draft: 'warning', approved: 'success', rejected: 'danger', superseded: 'neutral' };

const ConfidenceBars = {
  name: 'ConfidenceBars',
  props: { level: { type: String, default: 'low' }, compact: { type: Boolean, default: false } },
  computed: { filled() { return { high: 3, medium: 2, low: 1 }[this.level] || 1; } },
  template: `<span class="cbars" :class="{ 'cbars--compact': compact, ['cbars--'+level]: true }">
    <i v-for="n in 3" :key="n" :class="{ on: n <= filled }"></i>
  </span>`,
};

export default {
  name: 'AdminCampaignDrafts',
  components: { MusubiSectionCard, MusubiStatusPill, ConfidenceBars },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      draftId: null,
      // list
      drafts: [],
      loadingList: false,
      showArchived: false,
      rowBusy: null,
      // Client-side list filters. The API caps the list at 200 rows, so there is
      // nothing to gain from a round trip per keystroke.
      search: '',
      typeFilter: '',
      statusFilter: '',
      // manual edit (per-card)
      editing: { source: false, audience: false, flow: false, content: false },
      edit: { source: {}, audience: {}, flow: {}, content: {} },
      saving: false,
      editError: null,
      // review
      draft: null,
      loading: false,
      loadError: null,
      liveEstimate: null,
      estimateBusy: false,
      // actions
      busy: null,          // 'approve' | 'reject' | 'refine' | null
      actionError: null,
      approveResult: null,
      // DISCOVERY MODE opt-in. Default false → current behaviour (paused flow).
      startDiscovery: false,
      rejecting: false,
      rejectReason: '',
      refineText: '',
      refineError: null,
      // 503 ai_unavailable — render the notice as a temporary outage, not a
      // rejection of what the operator typed.
      aiDown: false,
    };
  },
  computed: {
    /** Source types actually present, so the dropdown never offers a dead end. */
    typeOptions() {
      return [...new Set(this.drafts.map((d) => d.source_type).filter(Boolean))].sort();
    },
    statusOptions() {
      return [...new Set(this.drafts.map((d) => d.status).filter(Boolean))].sort();
    },
    filtersActive() {
      return !!(this.search.trim() || this.typeFilter || this.statusFilter);
    },
    filteredDrafts() {
      const q = this.search.trim().toLowerCase();
      return this.drafts.filter((d) => {
        if (this.typeFilter && d.source_type !== this.typeFilter) return false;
        if (this.statusFilter && d.status !== this.statusFilter) return false;
        if (!q) return true;
        return [d.campaign_title, d.prompt_text, d.plan_summary, d.source_name, this.sourceLabel(d.source_type)]
          .some((f) => String(f || '').toLowerCase().includes(q));
      });
    },
    payload() { return (this.draft && this.draft.payload) || {}; },
    grounding() { return (this.draft && this.draft.grounding) || {}; },
    versions() { return (this.draft && this.draft.versions) || []; },
    content() { return this.payload.content || {}; },
    flowSteps() { return (this.payload.flow && this.payload.flow.steps) || []; },
    industryGrounding() { return this.grounding.industry || []; },
    locationGrounding() { return this.grounding.location || []; },
    groundedMinEmployees() { return (this.payload.audience && this.payload.audience.grounded && this.payload.audience.grounded.min_employees) || 0; },
    confidenceReasons() { return (this.grounding.confidence && this.grounding.confidence.reasons) || []; },
    confidenceReasonsText() { return this.confidenceReasons.join('\n'); },
    confTitle() { return { high: 'High', medium: 'Medium', low: 'Low' }[this.draft ? this.draft.confidence : 'low'] || 'Low'; },
    createdLinks() {
      const r = (this.draft && this.draft.result_refs) || {};
      return { flow: r.flow_id || null, source: r.source_id || null };
    },
    sourceName() { return (this.payload.source && this.payload.source.name) || '—'; },
    sourceTypeLabel() {
      return this.sourceLabel(this.payload.source && this.payload.source.type);
    },
    regionSummary() {
      const g = (this.payload.audience && this.payload.audience.grounded) || {};
      const regions = g.regions || [];
      const terms = (this.locationGrounding || []).map((r) => r.term);
      if (!regions.length && !terms.length) return 'National (no region set)';
      return `${terms.join(', ') || 'region'} · ${regions.join(', ') || '—'}`;
    },
    bodyParagraphs() {
      return String(this.content.body_text || '').split(/\n{2,}/).filter((p) => p.trim());
    },
    canEdit() { return this.draft && this.draft.status === 'draft' && !this.draft.archived_at; },
    anyEditing() { return this.editing.source || this.editing.audience || this.editing.flow || this.editing.content; },
  },
  mounted() {
    this.draftId = this.parseId();
    if (this.draftId) this.loadDraft();
    else this.loadList();
  },
  methods: {
    parseId() {
      const m = String(window.location.pathname).match(/\/acquire\/drafts\/(\d+)/);
      return m ? Number(m[1]) : null;
    },
    statusLabel(s) { return STATUS_LABELS[s] || s; },
    sourceLabel(t) { return SOURCE_LABELS[t] || t || '—'; },
    clearFilters() { this.search = ''; this.typeFilter = ''; this.statusFilter = ''; },
    statusTone(s) { return STATUS_TONE[s] || 'neutral'; },
    tokenise(text) {
      // split "Hi {first_name}, ..." into literal + {token} parts for pill rendering
      return String(text || '').split(/(\{[a-z0-9_]+\})/i).filter((s) => s !== '').map((s) => {
        const m = s.match(/^\{([a-z0-9_]+)\}$/i);
        return m ? { token: true, text: m[1] } : { token: false, text: s };
      });
    },
    async loadList() {
      this.loadingList = true;
      try {
        const res = await this.api.get('/api/v1/admin/drafts' + (this.showArchived ? '?archived=1' : ''));
        const p = res?.data ?? res;
        let rows = Array.isArray(p?.data) ? p.data : (Array.isArray(p) ? p : []);
        // When "Show archived" is on, the API returns both; show only archived so
        // the toggle reads as a filter, not an "everything" dump.
        if (this.showArchived) rows = rows.filter((d) => d.archived_at);
        this.drafts = rows;
      } catch (e) { /* empty state */ }
      this.loadingList = false;
    },
    toggleArchived() {
      this.showArchived = !this.showArchived;
      this.loadList();
    },
    async archiveRow(d) {
      this.rowBusy = d.id;
      try { await this.api.delete(`/api/v1/admin/drafts/${d.id}`); await this.loadList(); }
      catch (e) { /* stay */ }
      this.rowBusy = null;
    },
    async restoreRow(d) {
      this.rowBusy = d.id;
      try { await this.api.post(`/api/v1/admin/drafts/${d.id}/unarchive`, {}); await this.loadList(); }
      catch (e) { /* stay */ }
      this.rowBusy = null;
    },
    async loadDraft() {
      this.loading = true; this.loadError = null;
      try {
        const res = await this.api.get(`/api/v1/admin/drafts/${this.draftId}`);
        const p = res?.data ?? res;
        this.draft = p?.draft ?? p;
        this.$nextTick(() => this.scrollRail());
      } catch (e) {
        this.loadError = e?.response?.status === 404 ? 'Draft not found.' : (e?.response?.data?.error || e?.message || 'Failed to load the draft.');
      }
      this.loading = false;
    },
    scrollRail() {
      const el = this.$refs.refineBody;
      if (el) el.scrollTop = el.scrollHeight;
    },
    async refine() {
      const instruction = this.refineText.trim();
      if (!instruction) return;
      this.busy = 'refine'; this.refineError = null;
      try {
        const res = await this.api.post(`/api/v1/admin/drafts/${this.draftId}/refine`, { instruction });
        const p = res?.data ?? res;
        this.draft = p?.draft ?? p;
        this.refineText = '';
        this.liveEstimate = null;
        this.$nextTick(() => this.scrollRail());
      } catch (e) {
        this.refineError = apiErrorMessage(e, 'Refine failed — your draft is unchanged.');
        this.aiDown = isAiUnavailable(e);
      }
      this.busy = null;
    },
    async approve() {
      this.busy = 'approve'; this.actionError = null; this.approveResult = null;
      try {
        const res = await this.api.post(`/api/v1/admin/drafts/${this.draftId}/approve`, { discovery: this.startDiscovery });
        const p = res?.data ?? res;
        this.draft = p?.draft ?? p;
        this.approveResult = p?.degraded
          ? 'Approved. Your role lacks Outreach access, so the source and email template were created but the flow was not — ask an operator with Outreach to finish it.'
          : (this.startDiscovery
            ? 'Approved. Created the source, an email template and a DISCOVERING drip flow. It gathers + counts matches; nothing sends until you go live.'
            : 'Approved. Created the source, an email template and a PAUSED drip flow. Nothing sends until you activate it.');
      } catch (e) {
        this.actionError = e?.response?.data?.error || e?.message || 'Approve failed — nothing was created.';
      }
      this.busy = null;
    },
    async reject() {
      const reason = this.rejectReason.trim();
      if (!reason) return;
      this.busy = 'reject'; this.actionError = null;
      try {
        const res = await this.api.post(`/api/v1/admin/drafts/${this.draftId}/reject`, { reason });
        const p = res?.data ?? res;
        this.draft = p?.draft ?? p;
        this.rejecting = false;
      } catch (e) {
        this.actionError = e?.response?.data?.error || e?.message || 'Reject failed.';
      }
      this.busy = null;
    },

    // ── manual (non-AI) card editing ──────────────────────────────────────
    startEdit(card) {
      this.editError = null;
      const p = this.payload;
      if (card === 'source') {
        const s = p.source || {};
        this.edit.source = { mode: s.mode || 'create', type: s.type || 'local_search', name: s.name || '', location_hint: s.location_hint || '' };
      } else if (card === 'audience') {
        const a = p.audience || {};
        const minEmp = (a.grounded && a.grounded.min_employees) || this.parseMinStaff(a.size_terms);
        this.edit.audience = {
          industry: (a.industry_terms || []).join(', '),
          location: (a.location_terms || []).join(', '),
          min_employees: minEmp || '',
          company_status: (a.company_status || '').toString().toLowerCase(),
        };
      } else if (card === 'flow') {
        const f = p.flow || {};
        this.edit.flow = { name: f.name || '', steps: (f.steps || []).map((s) => ({ ...s })) };
      } else if (card === 'content') {
        const c = p.content || {};
        this.edit.content = { subject: c.subject || '', body_text: c.body_text || '' };
      }
      this.editing[card] = true;
    },
    cancelEdit(card) { this.editing[card] = false; this.editError = null; },
    parseMinStaff(terms) {
      for (const t of (terms || [])) { const m = String(t).match(/(\d+)/); if (m) return Number(m[1]); }
      return '';
    },
    addFlowStep(type) {
      if (type === 'email') this.edit.flow.steps.push({ type: 'email', label: 'Email', subject: '', body_text: '' });
      else this.edit.flow.steps.push({ type: 'wait', days: 3 });
    },
    removeFlowStep(i) { this.edit.flow.steps.splice(i, 1); },
    /** Build the {edits, changed_paths} for a card and PUT it. */
    async saveEdit(card) {
      let edits = {};
      let paths = [];
      if (card === 'source') {
        const s = this.edit.source;
        edits = { source: { mode: s.mode || 'create', type: s.type, name: s.name, location_hint: s.location_hint } };
        paths = ['source'];
      } else if (card === 'audience') {
        const a = this.edit.audience;
        const industry = a.industry.split(',').map((x) => x.trim()).filter(Boolean);
        const location = a.location.split(',').map((x) => x.trim()).filter(Boolean);
        const min = Number(a.min_employees) || 0;
        edits = { audience: {
          industry_terms: industry,
          location_terms: location,
          size_terms: min > 0 ? [`${min}+ staff`] : [],
          company_status: a.company_status || null,
        } };
        paths = ['audience'];
      } else if (card === 'flow') {
        const steps = this.edit.flow.steps.map((s) => s.type === 'email'
          ? { type: 'email', label: s.label || 'Email', subject: s.subject || '', body_text: s.body_text || '' }
          : { type: 'wait', days: Number(s.days) || 1 });
        edits = { flow: { name: this.edit.flow.name, steps } };
        paths = ['flow'];
      } else if (card === 'content') {
        edits = { content: { subject: this.edit.content.subject, body_text: this.edit.content.body_text } };
        paths = ['content'];
      }
      this.saving = true; this.editError = null;
      try {
        const res = await this.api.put(`/api/v1/admin/drafts/${this.draftId}`, { edits, changed_paths: paths });
        const pl = res?.data ?? res;
        this.draft = pl?.draft ?? pl;
        this.liveEstimate = null;
        this.editing[card] = false;
        this.$nextTick(() => this.scrollRail());
      } catch (e) {
        this.editError = e?.response?.data?.error || e?.message || 'Could not save your edit.';
      }
      this.saving = false;
    },
    async archiveCurrent() {
      this.busy = 'archive';
      try {
        const res = await this.api.delete(`/api/v1/admin/drafts/${this.draftId}`);
        const p = res?.data ?? res;
        this.draft = p?.draft ?? p;
      } catch (e) { this.actionError = e?.response?.data?.error || e?.message || 'Archive failed.'; }
      this.busy = null;
    },
    async restoreCurrent() {
      this.busy = 'archive';
      try {
        const res = await this.api.post(`/api/v1/admin/drafts/${this.draftId}/unarchive`, {});
        const p = res?.data ?? res;
        this.draft = p?.draft ?? p;
      } catch (e) { this.actionError = e?.response?.data?.error || e?.message || 'Restore failed.'; }
      this.busy = null;
    },
  },
};
</script>

<style scoped>
.musubi-page--drafts { padding: var(--m-space-6); max-width: 1180px; }
.musubi-page__head h1 { font-weight: 750; }
.text-tertiary { color: var(--m-text-tertiary); }
.ai-ico { color: var(--m-accent-2); }

/* AI temporarily unavailable — an outage, not a rejection, so amber not red. */
.dl-ai-down { color: #92400E; }

.dl-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 4px 0 14px; }
.dl-select, .dl-input {
  border: 1px solid var(--m-border); background: var(--m-bg-card); border-radius: 6px;
  font-size: 0.82rem; padding: 6px 10px; color: var(--m-text-primary); font-family: inherit;
}
.dl-input { min-width: 240px; flex: 1 1 240px; max-width: 360px; }
.dl-row__num--found .dl-row__n { color: var(--m-accent-2); }

/* teal AI button + indigo commit button */
.btn-ai {
  background: var(--m-accent-2); color: #fff; border: 1px solid var(--m-accent-2);
  font-weight: 650;
}
.btn-ai:hover:not(:disabled) { background: color-mix(in srgb, var(--m-accent-2) 82%, #000); color: #fff; }
.btn-commit {
  background: var(--m-accent); color: var(--m-accent-text); border: 1px solid var(--m-accent);
  font-weight: 700; padding: 9px 18px;
}
.btn-commit:hover:not(:disabled) { background: var(--m-accent-hover); }
.btn-ghost { background: transparent; border: 1px solid var(--m-border-strong); color: var(--m-text-secondary); font-weight: 600; }
.btn:disabled { opacity: .55; cursor: not-allowed; }

/* ── list ── */
.dl-hero {
  display: flex; align-items: center; gap: 12px; margin: 14px 0 18px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--m-accent-2) 12%, #fff), var(--m-bg-card) 70%);
  border: 1px solid color-mix(in srgb, var(--m-accent-2) 32%, #fff);
  border-radius: var(--m-radius-lg); padding: 14px 18px;
}
.dl-hero__tag { flex: none; background: var(--m-accent-2); color: #fff; font-weight: 700; font-size: 0.72rem; border-radius: 999px; padding: 3px 10px; }
.dl-hero__body { font-size: 0.9rem; color: var(--m-text-secondary); }
.dl-empty { color: var(--m-text-secondary); background: var(--m-bg-content); border: 1px dashed var(--m-border-strong); border-radius: var(--m-radius); padding: 16px; }
.dl-list { display: flex; flex-direction: column; gap: 8px; }
.dl-row {
  display: flex; align-items: center; gap: 16px; text-decoration: none;
  border: 1px solid var(--m-border); border-radius: var(--m-radius); background: var(--m-bg-card);
  padding: 12px 15px; transition: all .12s;
}
.dl-row:hover { border-color: var(--m-accent); box-shadow: var(--m-shadow-card); }
.dl-row__body { display: flex; flex-direction: column; gap: 3px; margin-right: auto; min-width: 0; }
.dl-row__title { font-weight: 650; color: var(--m-text-primary); font-size: 0.92rem; }
.dl-row__meta { font-size: 0.76rem; color: var(--m-text-tertiary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 520px; }
.dl-row__meta i { opacity: .5; margin-right: 4px; }
.dl-row__num { text-align: right; line-height: 1.1; }
.dl-row__n { font-weight: 700; color: var(--m-text-primary); }
.dl-row__nt { display: block; font-size: 0.66rem; color: var(--m-text-tertiary); }

/* ── review grid ── */
.draft-grid { display: grid; grid-template-columns: 1fr 344px; gap: 0; align-items: start; }
.draft-main { padding-right: 26px; border-right: 1px solid var(--m-border); min-width: 0; }

.dh-crumb { font-size: 0.76rem; color: var(--m-text-tertiary); margin-bottom: 6px; }
.dh-crumb a { color: var(--m-text-secondary); text-decoration: none; }
.draft-head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.draft-title { font-size: 1.3rem; font-weight: 800; margin: 0; color: var(--m-text-primary); }
.ai-tag {
  display: inline-flex; align-items: center; gap: 5px; font-size: 0.7rem; font-weight: 700;
  color: var(--m-accent-2); background: color-mix(in srgb, var(--m-accent-2) 14%, #fff);
  border-radius: 999px; padding: 3px 9px;
}

.dh-pills { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 12px 0 4px; }
.conf { display: inline-flex; align-items: center; gap: 7px; cursor: help; }
.conf__t { font-size: 0.78rem; font-weight: 650; color: var(--m-text-secondary); }
.receipt {
  display: inline-flex; align-items: center; gap: 5px; font-family: var(--m-font-mono); font-size: 0.72rem;
  color: var(--m-accent-2); background: color-mix(in srgb, var(--m-accent-2) 10%, #fff);
  border-radius: var(--m-radius); padding: 3px 8px;
}
.receipt i { font-size: 0.8rem; }

/* confidence bars (child component styles are global-ish via deep) */
:deep(.cbars) { display: inline-flex; align-items: flex-end; gap: 2px; height: 13px; }
:deep(.cbars i) { width: 4px; background: var(--m-border-strong); border-radius: 1px; display: inline-block; }
:deep(.cbars i:nth-child(1)) { height: 6px; }
:deep(.cbars i:nth-child(2)) { height: 9px; }
:deep(.cbars i:nth-child(3)) { height: 13px; }
:deep(.cbars i.on) { background: var(--m-accent-2); }
:deep(.cbars--low i.on) { background: var(--m-status-warning); }
:deep(.cbars--compact) { height: 11px; }

.plan-summary {
  display: flex; gap: 12px; margin: 16px 0;
  background: color-mix(in srgb, var(--m-accent-2) 9%, #fff);
  border-left: 3px solid var(--m-accent-2); border-radius: var(--m-radius);
  padding: 14px 16px;
}
.plan-summary__ico { color: var(--m-accent-2); font-size: 1.2rem; margin-top: 2px; }
.plan-summary p { margin: 0; font-size: 0.95rem; color: var(--m-text-primary); line-height: 1.5; }
.plan-summary__n { color: var(--m-text-secondary); }

.reason { margin: 8px 0; }
.reason summary { cursor: pointer; font-size: 0.82rem; font-weight: 600; color: var(--m-accent-2); }
.reason summary i { margin-right: 5px; }
.reason p, .reason__list { margin: 8px 0 0; font-size: 0.85rem; color: var(--m-text-secondary); line-height: 1.5; }
.reason__list { padding-left: 18px; }

.dsec-h { font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--m-text-tertiary); font-weight: 700; margin: 22px 0 10px; }

.dcard { border: 1px solid var(--m-border); border-radius: var(--m-radius-lg); padding: 16px; margin-bottom: 12px; background: var(--m-bg-card); }
.dcard-h { display: flex; align-items: baseline; gap: 10px; margin-bottom: 10px; }
.dcard-h .step { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; color: var(--m-text-tertiary); }
.dcard-h h4 { margin: 0; font-size: 0.98rem; font-weight: 700; color: var(--m-text-primary); }
.kv { display: flex; gap: 12px; padding: 4px 0; }
.kv .k { flex: none; width: 104px; font-size: 0.78rem; color: var(--m-text-tertiary); }
.kv .v { font-size: 0.88rem; color: var(--m-text-primary); }
.covnote { font-size: 0.76rem; color: var(--m-text-tertiary); margin: 8px 0 0; }

.tagrow { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.sic {
  display: inline-flex; align-items: center; gap: 6px; font-size: 0.8rem; color: var(--m-text-primary);
  background: var(--m-bg-content); border: 1px solid var(--m-border); border-radius: 999px; padding: 4px 11px 4px 5px;
}
.sic .code { font-family: var(--m-font-mono); font-size: 0.72rem; font-weight: 700; background: var(--m-accent-soft); color: var(--m-accent); border-radius: 999px; padding: 2px 7px; }
.sic--geo .code { background: color-mix(in srgb, var(--m-accent-2) 16%, #fff); color: var(--m-accent-2); }
.sic--new .code { background: color-mix(in srgb, var(--m-accent-2) 16%, #fff); color: var(--m-accent-2); }
.sic--miss { opacity: .7; }
.sic--miss .code { background: color-mix(in srgb, var(--m-status-warning) 18%, #fff); color: #92600a; }

.matchline { display: flex; align-items: baseline; gap: 8px; }
.matchline__n { font-size: 1.5rem; font-weight: 800; color: var(--m-text-primary); }
.matchline__t { font-size: 0.85rem; color: var(--m-text-secondary); }
.matchline__busy { font-size: 0.72rem; color: var(--m-text-tertiary); }

.flow { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.fstep { display: inline-flex; flex-direction: column; border-radius: var(--m-radius); padding: 8px 12px; font-size: 0.78rem; }
.fstep i { margin-bottom: 3px; }
.fstep--mail { background: var(--m-accent-soft); color: var(--m-accent); }
.fstep--wait { background: var(--m-bg-content); border: 1px dashed var(--m-border-strong); color: var(--m-text-secondary); flex-direction: row; align-items: center; gap: 6px; }
.fstep__l { font-weight: 700; }
.fstep__s { color: var(--m-text-secondary); font-size: 0.72rem; }
.farrow { color: var(--m-text-tertiary); }

.email-prev { border: 1px solid var(--m-border); border-radius: var(--m-radius); overflow: hidden; }
.email-head { display: flex; gap: 10px; align-items: baseline; padding: 10px 13px; background: var(--m-bg-content); border-bottom: 1px solid var(--m-border); }
.email-head__k { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); font-weight: 700; }
.email-head__v { font-size: 0.85rem; font-weight: 650; color: var(--m-text-primary); }
.email-body { padding: 13px; }
.email-body p { margin: 0 0 10px; font-size: 0.85rem; color: var(--m-text-primary); line-height: 1.55; }
.email-body p:last-child { margin-bottom: 0; }
.mtoken { font-family: var(--m-font-mono); font-size: 0.74rem; background: var(--m-accent-soft); color: var(--m-accent); border-radius: 4px; padding: 1px 5px; }

.draft-foot { margin-top: 18px; padding-top: 16px; border-top: 1px solid var(--m-border); }
.actions { display: flex; gap: 10px; align-items: center; }
/* Content area is light in BOTH themes (only the rail goes ink), so this
   text-safe dark amber is theme-invariant by construction — no dark-mode
   legibility concern here. Left as a literal hex rather than pointed at
   --m-status-warning (#D97706): that token is the flat accent amber (icons/
   chips), not WCAG-safe as small body text on white. No darker "warn-text"
   tier exists in _tokens.scss yet (custom.scss's .fbanner.warn has the same
   gap, hardcoded #92400E) — canonise a shared token in the polish sweep
   rather than inventing one here. */
.fineprint { display: flex; align-items: flex-start; gap: 7px; font-size: 0.76rem; color: #92600a; margin: 10px 0 0; }
.fineprint i { margin-top: 2px; }
/* DISCOVERY MODE — start-mode chooser above the approve button. */
.startmode { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
.startmode__opt { display: flex; align-items: flex-start; gap: 8px; font-size: 0.8rem; padding: 8px 10px;
  border: 1px solid var(--m-border, #e2e8f0); border-radius: var(--m-radius, 8px); cursor: pointer; }
.startmode__opt input { margin-top: 3px; }
.startmode__opt.on { border-color: #5EEAD4; background: #F0FDFA; }
.reject-box { margin-top: 12px; background: var(--m-bg-content); border: 1px solid var(--m-border); border-radius: var(--m-radius); padding: 12px; }
.reject-box__lbl { font-size: 0.78rem; font-weight: 650; color: var(--m-text-secondary); display: block; margin-bottom: 6px; }
.reject-box__act { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
.terminal-state { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

/* ── refine rail ── */
.refine { position: sticky; top: 12px; display: flex; flex-direction: column; max-height: calc(100vh - 24px); padding-left: 22px; }
.refine-h {
  display: flex; gap: 10px; align-items: flex-start; border-radius: var(--m-radius-lg) var(--m-radius-lg) 0 0;
  background: linear-gradient(135deg, var(--m-accent-2), color-mix(in srgb, var(--m-accent-2) 70%, #000));
  color: #fff; padding: 13px 15px;
}
.refine-h i { margin-top: 2px; }
.refine-h strong { display: block; font-size: 0.9rem; }
.refine-h span { font-size: 0.74rem; opacity: .92; line-height: 1.35; }
.refine-body { flex: 1; overflow-y: auto; background: var(--m-bg-card); border: 1px solid var(--m-border); border-top: none; padding: 12px; }
.rday { text-align: center; font-size: 0.68rem; color: var(--m-text-tertiary); margin: 4px 0 10px; }
.msg { display: flex; margin-bottom: 10px; }
.msg--user { justify-content: flex-end; }
.ubub { background: var(--m-accent); color: var(--m-accent-text); border-radius: 12px 12px 2px 12px; padding: 8px 11px; font-size: 0.8rem; max-width: 85%; }
.abub { background: color-mix(in srgb, var(--m-accent-2) 9%, #fff); border-radius: 12px 12px 12px 2px; padding: 8px 11px; font-size: 0.8rem; color: var(--m-text-primary); max-width: 90%; }
.ahead { display: block; font-size: 0.66rem; font-weight: 700; color: var(--m-accent-2); margin-bottom: 3px; }
.chg { display: inline-block; margin-top: 4px; font-size: 0.7rem; font-family: var(--m-font-mono); background: var(--m-bg-content); border-radius: 4px; padding: 1px 6px; color: var(--m-text-secondary); }
.refine-in { background: var(--m-bg-card); border: 1px solid var(--m-border); border-top: none; border-radius: 0 0 var(--m-radius-lg) var(--m-radius-lg); padding: 11px; }
.rin { border: 1px solid color-mix(in srgb, var(--m-accent-2) 40%, #fff); resize: vertical; }
.refine-in__foot { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; }
.rcost { font-size: 0.68rem; color: var(--m-text-tertiary); }
.rcost i { color: var(--m-accent-2); }
.refine-in--closed { font-size: 0.78rem; color: var(--m-text-tertiary); border-radius: 0 0 var(--m-radius-lg) var(--m-radius-lg); }

@media (max-width: 900px) {
  .draft-grid { grid-template-columns: 1fr; }
  .draft-main { padding-right: 0; border-right: none; }
  .refine { position: static; padding-left: 0; margin-top: 18px; max-height: none; }
}

/* ── list: link + archive affordance ── */
.dl-row { padding: 0; overflow: hidden; }
.dl-row__link { display: flex; align-items: center; gap: 16px; text-decoration: none; flex: 1; min-width: 0; padding: 12px 15px; }
.dl-row--archived { opacity: 0.66; }
.dl-row__arch {
  flex: none; background: transparent; border: none; border-left: 1px solid var(--m-border);
  color: var(--m-text-tertiary); padding: 0 14px; align-self: stretch; cursor: pointer; font-size: 0.9rem;
}
.dl-row__arch:hover:not(:disabled) { color: var(--m-accent); background: var(--m-bg-content); }
.dl-row__arch:disabled { opacity: .5; cursor: not-allowed; }
.btn-ghost { background: transparent; border: 1px solid var(--m-border-strong); color: var(--m-text-secondary); font-weight: 600; }
.btn-ghost:hover:not(:disabled) { border-color: var(--m-accent); color: var(--m-accent); }
.ph-spacer { margin-left: auto; }
.btn-arch { color: var(--m-text-tertiary); }

/* ── per-card edit affordance + editors (manual = indigo/neutral, never teal) ── */
.dcard-h { position: relative; }
.dcard-edit {
  margin-left: auto; background: transparent; border: 1px solid var(--m-border-strong); border-radius: 999px;
  color: var(--m-text-secondary); font-size: 0.72rem; font-weight: 650; padding: 3px 11px; cursor: pointer;
}
.dcard-edit:hover { border-color: var(--m-accent); color: var(--m-accent); }
.dcard-edit i { margin-right: 4px; }
.dedit { display: flex; flex-direction: column; gap: 12px; }
.dfield { display: flex; flex-direction: column; gap: 5px; }
.dfield--inline { flex-direction: row; align-items: center; gap: 8px; }
.dfield__l { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-secondary); }
.dfield__h { font-size: 0.72rem; color: var(--m-text-tertiary); }
.dfield-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 12px; }
.dinput {
  border: 1px solid var(--m-border-strong); border-radius: var(--m-radius); padding: 8px 10px;
  font: inherit; font-size: 0.86rem; color: var(--m-text-primary); background: var(--m-bg-content); resize: vertical; width: 100%;
}
.dinput:focus { outline: none; border-color: var(--m-accent); }
.dinput--sm { max-width: 90px; }
.mb-1 { margin-bottom: 6px; }
.dedit__act { display: flex; gap: 8px; }
.fstep-edit { border: 1px solid var(--m-border); border-radius: var(--m-radius); padding: 11px; background: var(--m-bg-content); }
.fstep-edit__h { display: flex; align-items: center; gap: 8px; margin-bottom: 7px; }
.fstep-edit__n { font-size: 0.72rem; font-weight: 700; color: var(--m-text-secondary); }
.fstep-edit__type { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 999px; padding: 1px 8px; }
.fstep-edit__rm { margin-left: auto; background: transparent; border: none; color: var(--m-text-tertiary); cursor: pointer; }
.fstep-edit__rm:hover { color: var(--m-status-danger); }
.fstep-add { display: flex; gap: 8px; }
.btn-xs { padding: 4px 10px; font-size: 0.74rem; border-radius: 7px; }
.editnote { font-size: 0.74rem; color: var(--m-text-tertiary); margin: 8px 0 0; }

/* manual-edit history bubble — indigo (commit), distinct from teal AI */
.msg--manual { justify-content: flex-start; }
.mbub { background: var(--m-accent-soft); border-radius: 12px 12px 12px 2px; padding: 8px 11px; font-size: 0.8rem; color: var(--m-text-primary); max-width: 90%; }
.mhead { display: block; font-size: 0.66rem; font-weight: 700; color: var(--m-accent); margin-bottom: 3px; }
</style>
